import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Globe, ExternalLink, ToggleLeft, ToggleRight, Rocket, CheckCircle2, AlertCircle } from 'lucide-react';

interface Site {
  id: string;
  name: string;
  slug: string;
  domain: string;
  status: string;
  githubRepo: string | null;
  previewUrl: string;
  _count: {
    deployments: number;
  };
}

interface SiteFolder {
  name: string;
  hasIndex: boolean;
  isRegistered: boolean;
}

export default function Sites() {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetch('/api/sites')
      .then((r) => r.json())
      .then((data) => {
        setSites(data.sites || []);
        setLoading(false);
      });
  }, []);

  const toggleMaintenance = async (site: Site) => {
    const enabled = site.status !== 'MAINTENANCE';
    await fetch(`/api/sites/${site.id}/maintenance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    setSites(
      sites.map((s) =>
        s.id === site.id ? { ...s, status: enabled ? 'MAINTENANCE' : 'ACTIVE' } : s
      )
    );
  };

  const triggerDeploy = async (siteId: string) => {
    await fetch(`/api/sites/${siteId}/deploy`, { method: 'POST' });
    // Refresh sites
    const res = await fetch('/api/sites');
    const data = await res.json();
    setSites(data.sites || []);
  };

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-green-500',
    MAINTENANCE: 'bg-yellow-500',
    DISABLED: 'bg-gray-500',
    DEPLOYING: 'bg-blue-500 animate-pulse',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Sites</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Site
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site) => (
          <div
            key={site.id}
            className="bg-gray-800 rounded-xl p-6 hover:ring-2 hover:ring-primary-500/50 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-700">
                  <Globe className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <Link
                    to={`/sites/${site.id}`}
                    className="font-semibold text-white hover:text-primary-400"
                  >
                    {site.name}
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>{site.domain}</span>
                    <a
                      href={`https://${site.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-400"
                      title="Visit production"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <span className="text-gray-600">|</span>
                    <a
                      href={site.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-400 flex items-center gap-1"
                      title="Preview site"
                    >
                      <Globe className="w-3 h-3" />
                      <span className="text-xs">Preview</span>
                    </a>
                  </div>
                </div>
              </div>
              <div
                className={`w-2 h-2 rounded-full ${statusColors[site.status] || 'bg-gray-500'}`}
                title={site.status}
              />
            </div>

            <div className="text-sm text-gray-400 mb-4">
              {site.githubRepo ? (
                <span>Connected to {site.githubRepo}</span>
              ) : (
                <span className="text-gray-500">No GitHub repo connected</span>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleMaintenance(site)}
                  className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title={
                    site.status === 'MAINTENANCE'
                      ? 'Disable maintenance mode'
                      : 'Enable maintenance mode'
                  }
                >
                  {site.status === 'MAINTENANCE' ? (
                    <ToggleRight className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <ToggleLeft className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {site.githubRepo && (
                  <button
                    onClick={() => triggerDeploy(site.id)}
                    className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                    title="Trigger deployment"
                  >
                    <Rocket className="w-5 h-5 text-gray-400 hover:text-primary-400" />
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {site._count?.deployments ?? 0} deployments
              </span>
            </div>
          </div>
        ))}
      </div>

      {sites.length === 0 && (
        <div className="text-center py-12">
          <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No sites yet</h3>
          <p className="text-gray-400 mb-4">
            Create your first site to get started
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium"
          >
            Add Site
          </button>
        </div>
      )}

      {/* Create Site Modal */}
      {showCreateModal && (
        <CreateSiteModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(site) => {
            setSites([site, ...sites]);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

interface CreateSiteModalProps {
  onClose: () => void;
  onCreated: (site: Site) => void;
}

function CreateSiteModal({ onClose, onCreated }: CreateSiteModalProps) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    domain: '',
    description: '',
    useExistingFolder: false,
    existingFolder: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableFolders, setAvailableFolders] = useState<SiteFolder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Fetch available folders when "use existing folder" is checked
  useEffect(() => {
    if (form.useExistingFolder) {
      setLoadingFolders(true);
      fetch('/api/sites/folders')
        .then((r) => r.json())
        .then((data) => {
          setAvailableFolders(data);
          setLoadingFolders(false);
        })
        .catch(() => {
          setLoadingFolders(false);
        });
    }
  }, [form.useExistingFolder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/sites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create site');
      }

      const site = await res.json();
      onCreated(site);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold text-white mb-6">Create Site</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="My Marketing Site"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Slug
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })
              }
              placeholder="my-site"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Domain
            </label>
            <input
              type="text"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              placeholder="example.com"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description (optional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of the site"
              rows={2}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="pt-4 border-t border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.useExistingFolder}
                onChange={(e) => setForm({ ...form, useExistingFolder: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-300">
                Use existing site folder
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Check this if you already have site files in the sites directory
            </p>
          </div>

          {form.useExistingFolder && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Select Existing Folder
              </label>
              {loadingFolders ? (
                <div className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400">
                  Loading folders...
                </div>
              ) : availableFolders.length === 0 ? (
                <div className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400">
                  No folders found in sites directory
                </div>
              ) : (
                <>
                  <select
                    value={form.existingFolder}
                    onChange={(e) => setForm({ ...form, existingFolder: e.target.value, domain: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required={form.useExistingFolder}
                  >
                    <option value="">-- Select a folder --</option>
                    {availableFolders.map((folder) => (
                      <option key={folder.name} value={folder.name} disabled={folder.isRegistered}>
                        {folder.name}
                        {folder.isRegistered ? ' (already registered)' : ''}
                        {!folder.hasIndex ? ' (no index.html)' : ''}
                      </option>
                    ))}
                  </select>
                  {form.existingFolder && (
                    <div className="mt-2 space-y-1">
                      {availableFolders.find(f => f.name === form.existingFolder)?.hasIndex ? (
                        <div className="flex items-center gap-2 text-xs text-green-400">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Contains index.html</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-yellow-400">
                          <AlertCircle className="w-4 h-4" />
                          <span>Warning: No index.html found</span>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select a folder from the sites directory
                  </p>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg font-medium"
            >
              {loading ? 'Creating...' : 'Create Site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
